/**
 * System Template Type Definitions
 *
 * Shared interfaces for system templates that ship with the product.
 * Templates are read-only and can be copied by users to create their own entities.
 *
 * @see Issue #400 - System Templates for First-Time User Experience
 */

import type { SignerPolicy } from '../dtos/signer-policy.dto';

// =============================================================================
// BASE TEMPLATE INTERFACE
// =============================================================================

/**
 * Base interface for all system templates.
 * Generic type T represents the template-specific data payload.
 */
export interface SystemTemplate<T = unknown> {
  /** Unique identifier (e.g., 'tpl-seq-new-lead') */
  id: string;
  /** Display name */
  name: string;
  /** 1-2 sentence description */
  description: string;
  /** Category for grouping/filtering */
  category: string;
  /** Material icon name */
  icon: string;
  /** Accent color (hex) */
  color: string;
  /** Searchable tags */
  tags: string[];
  /** Estimated duration (e.g., "14 days") */
  estimatedDuration?: string;
  /** Complexity level */
  complexity: TemplateComplexity;
  /** Industries this template fits */
  industryFit: TemplateIndustry[];
  /** Template-specific payload */
  data: T;
}

export type TemplateComplexity = 'beginner' | 'intermediate' | 'advanced';

export type TemplateIndustry = 'real_estate' | 'lending' | 'mortgage' | 'fintech' | 'insurance' | 'finance' | 'general';

// =============================================================================
// SEQUENCE TEMPLATE TYPES
// =============================================================================

export type SequenceCategory =
  | 'lead-generation'
  | 'nurture'
  | 'follow-up'
  | 're-engagement'
  | 'referral'
  | 'onboarding';

export type SequenceStepType = 'EMAIL' | 'DELAY' | 'TASK' | 'CONDITION';

export interface SequenceStepTemplate {
  /** Step order (1-based) */
  order: number;
  /** Step name */
  name: string;
  /** Type of step */
  stepType: SequenceStepType;
  /** Days to delay before this step */
  delayDays?: number;
  /** Hours to delay before this step */
  delayHours?: number;
  /** Email subject (supports {{variables}}) */
  emailSubject?: string;
  /** Email body HTML (supports {{variables}}) */
  emailBody?: string;
  /** Plain text version of email */
  emailText?: string;
  /** Task title if stepType is TASK */
  taskTitle?: string;
  /** Task description if stepType is TASK */
  taskDescription?: string;
}

export interface SequenceTemplateData {
  /** Trigger type for the sequence */
  triggerType: 'manual' | 'event' | 'segment';
  /** Trigger configuration */
  triggerConfig?: Record<string, unknown>;
  /** Goal type */
  goalType?: 'conversion' | 'engagement' | 'reply';
  /** Goal configuration */
  goalConfig?: Record<string, unknown>;
  /** Whether to send on weekends */
  sendOnWeekends: boolean;
  /** Start hour for sending (0-23) */
  sendHoursStart: number;
  /** End hour for sending (0-23) */
  sendHoursEnd: number;
  /** Sequence steps */
  steps: SequenceStepTemplate[];
}

export type SequenceTemplate = SystemTemplate<SequenceTemplateData>;

// =============================================================================
// CAMPAIGN TEMPLATE TYPES
// =============================================================================

export type CampaignCategory = 'announcement' | 'newsletter' | 'seasonal' | 'promotional' | 'educational';

export type TemplateCampaignChannel = 'email' | 'voice' | 'sms';

export interface CampaignTemplateData {
  /** Type of campaign */
  campaignType: TemplateCampaignChannel;
  /** Email subject (supports {{variables}}) */
  subject?: string;
  /** Email body HTML (supports {{variables}}) */
  content?: string;
  /** Plain text version */
  textContent?: string;
  /** From name suggestion */
  fromNameSuggestion?: string;
  /** Voice script if campaignType is 'voice' */
  voiceScript?: string;
  /** Recommended send time */
  recommendedSendTime?: string;
  /** Recommended audience segment */
  recommendedSegment?: string;
}

export type CampaignTemplate = SystemTemplate<CampaignTemplateData>;

// =============================================================================
// SAVED SEARCH TEMPLATE TYPES
// =============================================================================

export type SavedSearchCategory = 'pipeline' | 'engagement' | 'performance' | 'compliance' | 'reporting';

export type SearchableEntity = 'case' | 'consumer' | 'partner' | 'lead' | 'task' | 'referral';

export type SearchFilterOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'starts_with'
  | 'ends_with'
  | 'greater_than'
  | 'less_than'
  | 'between'
  | 'in'
  | 'not_in'
  | 'is_null'
  | 'is_not_null';

export interface SavedSearchFilter {
  /** Field to filter on */
  field: string;
  /** Filter operator */
  operator: SearchFilterOperator;
  /** Filter value (supports {{today}}, {{currentUserId}} placeholders) */
  value: string | number | boolean | string[] | number[] | null;
  /** Second value for 'between' operator */
  secondValue?: string | number;
}

export interface SavedSearchSort {
  /** Field to sort by */
  field: string;
  /** Sort direction */
  direction: 'asc' | 'desc';
}

export interface SavedSearchTemplateData {
  /** Entity type this search applies to */
  entity: SearchableEntity;
  /** Filter criteria */
  filters: SavedSearchFilter[];
  /** Sort criteria */
  sorts: SavedSearchSort[];
  /** Whether to pin by default */
  isPinned: boolean;
}

export type SavedSearchTemplate = SystemTemplate<SavedSearchTemplateData>;

// =============================================================================
// PIPELINE TEMPLATE TYPES
// =============================================================================

export type PipelineCategory = 'sales' | 'lending' | 'support' | 'operations' | 'partnerships';

export interface PipelineStageTemplate {
  /** Stage order (1-based) */
  order: number;
  /** Stage name */
  name: string;
  /** Stage description */
  description?: string;
  /** Stage color (hex) */
  color: string;
  /** Stage icon */
  icon?: string;
  /** Whether this is a terminal/closed stage */
  isTerminal: boolean;
  /** Whether this is a success stage (for win/loss tracking) */
  isSuccess?: boolean;
  /** Default SLA hours for this stage */
  defaultSlaHours?: number;
  /** Required fields to exit this stage */
  requiredFields?: string[];
}

export interface PipelineTemplateData {
  /** Entity types this pipeline can be used for */
  entityTypes: string[];
  /** Pipeline stages */
  stages: PipelineStageTemplate[];
  /** Default SLA configuration */
  defaultSlaConfig?: {
    warningThresholdPercent: number;
    enableNotifications: boolean;
  };
}

export type PipelineTemplate = SystemTemplate<PipelineTemplateData>;

// =============================================================================
// TERMSHEET TEMPLATE TYPES
// =============================================================================

export type TermsheetCategory = 'short-term' | 'rental' | 'development' | 'commercial';

export type TermsheetLoanType =
  | 'bridge'
  | 'fix_and_flip'
  | 'dscr'
  | 'construction'
  | 'ground_up'
  | 'commercial'
  | 'multifamily'
  | 'mixed_use';

export interface TermsheetFieldTemplate {
  /** Field name */
  name: string;
  /** Field label */
  label: string;
  /** Field type */
  type: 'text' | 'number' | 'percent' | 'currency' | 'select' | 'boolean';
  /** Default value */
  defaultValue?: string | number | boolean;
  /** Options for select fields */
  options?: string[];
  /** Whether field is required */
  required: boolean;
  /** Help text */
  helpText?: string;
}

export interface TermsheetTemplateData {
  /** Loan type */
  loanType: TermsheetLoanType;
  /** Typical loan amount range */
  loanAmountRange: {
    min: number;
    max: number;
  };
  /** Typical LTV range */
  ltvRange: {
    min: number;
    max: number;
  };
  /** Typical interest rate range */
  interestRateRange: {
    min: number;
    max: number;
  };
  /** Typical term in months */
  termMonths: {
    min: number;
    max: number;
  };
  /** Origination points range */
  originationPoints: {
    min: number;
    max: number;
  };
  /** Template fields */
  fields: TermsheetFieldTemplate[];
  /** Key features/highlights */
  keyFeatures: string[];
  /** Ideal borrower profile */
  idealBorrower: string;
  /**
   * Canonical signer policy for the term sheet (Issue #1309).
   * When a user copies this system template, the materialised business
   * template inherits this policy so `resolveSigners` has something to
   * iterate against. Admins can override the policy in the template editor.
   *
   * Optional so historically authored templates (pre-#1309) remain valid;
   * all current system templates populate it.
   */
  signerPolicy?: SignerPolicy;
}

export type TermsheetTemplate = SystemTemplate<TermsheetTemplateData>;

// =============================================================================
// INTAKE FLOW TEMPLATE TYPES (Issue #432, Issue #471)
// =============================================================================

export type IntakeFlowCategory = 'short-term' | 'rental' | 'development' | 'commercial' | 'general';

export type IntakeFlowType =
  | 'bridge'
  | 'construction'
  | 'rental'
  | 'commercial'
  | 'general'
  | 'partner_invitation'
  | 'partner_onboarding';

/**
 * Display mode for intake wizard
 * - WIZARD: Step-by-step navigation with previous/next buttons (default)
 * - SCROLL: Infinite scroll view with all sections visible at once
 */
export enum IntakeDisplayMode {
  /** Step-by-step wizard with previous/next navigation */
  WIZARD = 'wizard',
  /** Infinite scroll with all sections visible, sidebar highlights on scroll */
  SCROLL = 'scroll',
}

export type IntakeFieldType =
  | 'text'
  | 'select'
  | 'multi-select'
  | 'currency'
  | 'percentage'
  | 'address'
  | 'date'
  | 'checkbox'
  | 'ssn'
  | 'phone'
  | 'email'
  | 'number'
  | 'radio'
  // Extended field types (Issue #471)
  | 'file'
  | 'signature'
  | 'rich-text'
  | 'slider'
  | 'rating'
  | 'time'
  | 'datetime'
  | 'url'
  | 'color'
  | 'textarea'
  | 'date-range'
  | 'hidden'
  // Admin intake field types (Business Creation)
  | 'dynamic-select' // Select with options loaded from API
  | 'pricing-cards' // Subscription tier card selector
  | 'summary' // Review/summary display of form data
  | 'radio-cards' // Radio selection displayed as cards (partner type selection)
  | 'info-card' // Read-only information card
  | 'readonly-summary' // Read-only summary display
  | 'masked-input' // Input with masking (e.g., SSN, phone)
  | 'file-upload' // File upload field
  | 'password' // Password input
  | 'document-preview' // Document preview/view
  | 'esign-embed' // Embedded e-signature component
  | 'success-card'; // Success confirmation card

/**
 * Conditional operator for field/step visibility
 */
export type IntakeConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'not_contains'
  | 'greater_than'
  | 'less_than'
  | 'greater_than_or_equals'
  | 'less_than_or_equals'
  | 'is_empty'
  | 'is_not_empty'
  | 'in'
  | 'not_in'
  | 'starts_with'
  | 'ends_with';

/**
 * Single condition for conditional display
 */
export interface IntakeCondition {
  /** Field to check */
  field: string;
  /** Comparison operator */
  operator: IntakeConditionOperator;
  /** Value to compare against */
  value?: string | number | boolean | string[] | number[];
}

/**
 * Condition group with AND/OR logic (Issue #471)
 */
export interface IntakeConditionGroup {
  /** Logic operator for combining conditions */
  logic: 'AND' | 'OR';
  /** Conditions in this group */
  conditions: Array<IntakeCondition | IntakeConditionGroup>;
}

/**
 * Field validation rules with custom messages (Issue #471)
 */
export interface IntakeFieldValidation {
  /** Minimum value/length */
  min?: number;
  /** Maximum value/length */
  max?: number;
  /** Minimum length for text fields */
  minLength?: number;
  /** Maximum length for text fields */
  maxLength?: number;
  /** Regex pattern */
  pattern?: string;
  /** Custom error message (supports i18n keys) */
  message?: string;
  /** Custom error messages per validation type */
  messages?: {
    required?: string;
    min?: string;
    max?: string;
    minLength?: string;
    maxLength?: string;
    pattern?: string;
    custom?: string;
    matchField?: string;
  };
  /** Custom validation function name (client-side) */
  customValidator?: string;
  /** Allowed file types for file upload (e.g., '.pdf,.doc,.jpg') */
  allowedTypes?: string[];
  /** Max file size in bytes */
  maxFileSize?: number;
  /** Step for number/slider inputs */
  step?: number;
  /** Field name to match for password confirmation */
  matchField?: string;
}

/**
 * Multi-language label support (Issue #471)
 */
export interface IntakeI18nLabel {
  /** Default label (English) */
  default: string;
  /** Translations by language code (e.g., 'es', 'fr', 'zh') */
  translations?: Record<string, string>;
}

/**
 * Field template for intake wizard steps (Issue #471 enhanced)
 */
export interface IntakeFieldTemplate {
  /** Unique field identifier */
  id: string;
  /** Field name (for form state) */
  name: string;
  /** Display label (string for simple, object for i18n) */
  label: string | IntakeI18nLabel;
  /** Field input type */
  type: IntakeFieldType;
  /** Whether field is required */
  required: boolean;
  /** Validation rules */
  validation?: IntakeFieldValidation;
  /** Whether field contains sensitive data (SSN, etc.) */
  sensitive?: boolean;
  /** Placeholder text (supports i18n) */
  placeholder?: string | IntakeI18nLabel;
  /** Help text below field (supports i18n) */
  helpText?: string | IntakeI18nLabel;
  /** Options for select/radio fields */
  options?: Array<{ value: string; label: string | IntakeI18nLabel }>;
  /** Default value */
  defaultValue?: string | number | boolean;
  /** Conditional display rules (legacy - single condition) */
  conditionalDisplay?: IntakeCondition;
  /** Advanced conditional display (AND/OR groups) - Issue #471 */
  conditionalDisplayGroup?: IntakeConditionGroup;
  /** Grid width (1-12) */
  width?: number;
  /** Read-only field */
  readOnly?: boolean;
  /** Disabled field */
  disabled?: boolean;
  /** Auto-focus this field */
  autoFocus?: boolean;
  /** Show field only if previous field is filled (Issue #471) */
  showAfterField?: string;
  /** Role-based visibility (Issue #471) */
  visibleToRoles?: string[];
  /** File upload config (for type='file') */
  fileConfig?: {
    accept?: string[];
    maxSize?: number;
    multiple?: boolean;
  };

  // ==========================================================================
  // Admin Intake Field Configurations (Business Creation)
  // ==========================================================================

  /**
   * Dynamic select config (for type='dynamic-select')
   * Options are loaded from an API endpoint at runtime
   */
  dynamicSelectConfig?: {
    /** Unique key identifying the data source (e.g., 'realms', 'industries', 'users') */
    dataSource: string;
    /** Field to use as option value */
    valueField?: string;
    /** Field to use as option label */
    labelField?: string;
    /** Field to use as option description (for rich display) */
    descriptionField?: string;
    /** Field to use as option icon (Material icon name) */
    iconField?: string;
    /** Allow selecting multiple values */
    multiple?: boolean;
    /** Additional fields to include in option data */
    includeFields?: string[];
    /** Filter parameters to pass to the API */
    filterParams?: Record<string, unknown>;
    /** Empty state message */
    emptyMessage?: string;
    /** Loading message */
    loadingMessage?: string;
    /** Field dependency for filtering - field name or filter object */
    filterBy?: string | Record<string, unknown>;
    /**
     * Optional link rendered above the selection UI, letting the user jump
     * to the source-of-truth screen that manages these options (e.g. a
     * template manager, tag editor, role editor). Opens in a new tab.
     */
    managementLink?: {
      /** Target URL (relative or absolute) */
      href: string;
      /** i18n key for the link label (falls back to the key itself if missing) */
      labelKey: string;
    };
  };

  /**
   * Pricing cards config (for type='pricing-cards')
   * Displays subscription tiers as selectable cards
   */
  pricingCardsConfig?: {
    /** Pricing tier options */
    tiers: Array<{
      /** Tier value (stored in form data) */
      value: string;
      /** Display label */
      label: string;
      /** Short description */
      description: string;
      /** Price display (e.g., '$49/month', 'Custom pricing') */
      price: string;
      /** Annual price for comparison */
      annualPrice?: string;
      /** List of features included */
      features: string[];
      /** Whether this is the recommended/popular option */
      recommended?: boolean;
      /** Badge text (e.g., 'Most Popular', 'Best Value') */
      badge?: string;
    }>;
    /** Show billing cycle toggle */
    showBillingCycleToggle?: boolean;
    /** Billing cycle field name (if toggle is shown) */
    billingCycleFieldName?: string;
  };

  /**
   * Summary config (for type='summary')
   * Displays a read-only summary of collected form data
   */
  summaryConfig?: {
    /** Sections to display in summary */
    sections: Array<{
      /** Section title */
      title: string;
      /** Field names to include in this section */
      fields: Array<{
        /** Field name from form data */
        name: string;
        /** Display label */
        label: string;
        /** Format type for display */
        format?: 'text' | 'currency' | 'date' | 'boolean' | 'list' | 'lookup' | 'percentage';
        /** For 'lookup' format: map of value -> display label */
        lookupMap?: Record<string, string>;
      }>;
    }>;
    /** Show edit button to go back to specific section */
    showEditButtons?: boolean;
  };
  /**
   * Radio cards config (for type='radio-cards')
   * Displays radio options as selectable cards (e.g., partner type selection)
   */
  radioCardsConfig?: {
    /** Radio card options */
    options: Array<{
      /** Option value */
      value: string;
      /** Display label */
      label: string;
      /** Card description */
      description?: string;
      /** Icon name */
      icon?: string;
      /** Whether this option is recommended */
      recommended?: boolean;
    }>;
    /** Display orientation */
    orientation?: 'horizontal' | 'vertical';
    /** Number of columns for card grid */
    columns?: number;
  };

  /**
   * Info card config (for type='info-card')
   * Displays read-only information in a card format
   */
  infoCardConfig?: {
    /** Card title */
    title?: string;
    /** Card message */
    message?: string;
    /** Card description */
    description?: string;
    /** Card variant */
    variant?: 'info' | 'success' | 'warning' | 'error' | 'highlighted';
    /** Icon name */
    icon?: string;
    /** Show icon */
    showIcon?: boolean;
  };

  /**
   * Readonly summary config (for type='readonly-summary')
   * Displays read-only summary of form data
   */
  readonlySummaryConfig?: {
    /** Fields to display */
    fields: Array<{
      /** Field name */
      name: string;
      /** Display label */
      label: string;
    }>;
  };

  /**
   * Masked input config (for type='masked-input')
   * Input field with masking (e.g., SSN, phone numbers)
   */
  maskedInputConfig?: {
    /** Input mask pattern (e.g., '999-99-9999' for SSN) */
    mask: string;
    /** Placeholder character for unfilled positions */
    placeholderChar?: string;
    /** Show mask on focus */
    showMaskOnFocus?: boolean;
    /** Placeholder character for masked input */
    placeholder?: string;
  };

  /**
   * File upload config (for type='file-upload')
   * File upload field with preview
   */
  fileUploadConfig?: {
    /** Accepted file types (comma-separated extensions, e.g., '.pdf,.jpg,.png') */
    accept?: string;
    /** Maximum file size in bytes */
    maxSize?: number;
    /** Maximum file size in MB (alternative to maxSize) */
    maxSizeMB?: number;
    /** Maximum number of files */
    maxFiles?: number;
    /** Allow multiple files */
    multiple?: boolean;
    /** Show preview for images */
    showPreview?: boolean;
    /** Upload endpoint URL */
    uploadUrl?: string;
    /** Upload endpoint */
    uploadEndpoint?: string;
  };

  /**
   * Password config (for type='password')
   * Password input with strength indicator
   */
  passwordConfig?: {
    /** Show password strength indicator */
    showStrength?: boolean;
    /** Minimum password length */
    minLength?: number;
    /** Require uppercase letters */
    requireUppercase?: boolean;
    /** Require lowercase letters */
    requireLowercase?: boolean;
    /** Require numbers */
    requireNumbers?: boolean;
    /** Require special characters */
    requireSpecial?: boolean;
    /** Show toggle visibility button */
    showToggle?: boolean;
  };

  /**
   * Document preview config (for type='document-preview')
   * Displays document preview with download option
   */
  documentPreviewConfig?: {
    /** Document URL or field name containing URL */
    documentUrl?: string;
    /** Source field name containing document URL */
    sourceField?: string;
    /** Document title */
    title?: string;
    /** Allow download */
    allowDownload?: boolean;
    /** Preview type */
    previewType?: 'pdf' | 'image' | 'iframe';
    /** Preview height in pixels */
    height?: number;
  };

  /**
   * E-sign embed config (for type='esign-embed')
   * Embedded e-signature component (e.g., DocuSign, HelloSign)
   */
  esignEmbedConfig?: {
    /** E-sign provider */
    provider?: 'docusign' | 'hellosign' | 'custom';
    /** Document ID or URL */
    documentId?: string;
    /** Field name containing envelope/document ID */
    envelopeIdField?: string;
    /** Signer email field name */
    signerEmailField?: string;
    /** Signer name field name */
    signerNameField?: string;
    /** Recipient role in signing order */
    recipientRole?: string;
    /** Callback URL after signing */
    callbackUrl?: string;
    /** Show completion message */
    showCompletionMessage?: boolean;
    /** Embed height in pixels */
    height?: number;
    /** Action on completion */
    onComplete?: 'advance' | 'stay' | 'redirect';
  };

  /**
   * Success card config (for type='success-card')
   * Displays success message with optional actions
   */
  successCardConfig?: {
    /** Success message */
    message?: string;
    /** Card title */
    title?: string;
    /** Card description */
    description?: string;
    /** Subtitle */
    subtitle?: string;
    /** Show icon */
    showIcon?: boolean;
    /** Primary action button */
    primaryAction?: {
      label: string;
      url?: string;
      action?: string;
    };
    /** Secondary action button */
    secondaryAction?: {
      label: string;
      url?: string;
      action?: string;
    };
    /** Action buttons */
    actions?: Array<{
      label: string;
      href?: string;
      url?: string;
      action?: string;
      variant?: 'primary' | 'secondary';
    }>;
    /** Show confetti animation */
    confetti?: boolean;
  };
}

/**
 * Section within a step (Issue #471)
 * Allows grouping fields within a step
 */
export interface IntakeStepSection {
  /** Unique section identifier */
  id: string;
  /** Section title */
  title: string | IntakeI18nLabel;
  /** Section description */
  description?: string | IntakeI18nLabel;
  /** Section icon */
  icon?: string;
  /** Collapsible section */
  collapsible?: boolean;
  /** Start collapsed */
  collapsed?: boolean;
  /** Field IDs in this section (order matters) */
  fieldIds?: string[];
  /**
   * Dynamic source for generating sections at runtime.
   * When set, sections are auto-generated per item in the referenced data source
   * (e.g., 'selectedContracts' generates one section per selected contract type).
   * Mutually exclusive with fieldIds — use one or the other.
   */
  dynamicSource?: string;
  /** Conditional display for section */
  conditionalDisplay?: IntakeCondition;
  /** Advanced conditional display */
  conditionalDisplayGroup?: IntakeConditionGroup;
}

/**
 * Step template for intake wizard (Issue #471 enhanced)
 */
export interface IntakeStepTemplate {
  /** Unique step identifier */
  id: string;
  /** Step order (0-based) */
  order: number;
  /** Step display name */
  name: string | IntakeI18nLabel;
  /** Step description */
  description: string | IntakeI18nLabel;
  /** Material icon name */
  icon: string;
  /** Whether this step requires authentication */
  requiresAuth: boolean;
  /** Fields in this step */
  fields: IntakeFieldTemplate[];
  /** Sections for grouping fields (Issue #471) */
  sections?: IntakeStepSection[];
  /** Conditional display for entire step (legacy) */
  conditionalDisplay?: IntakeCondition;
  /** Advanced conditional display (AND/OR) - Issue #471 */
  conditionalDisplayGroup?: IntakeConditionGroup;
  /** Step-level validation message */
  validationMessage?: string | IntakeI18nLabel;
  /** Custom submit button text for this step */
  submitButtonText?: string | IntakeI18nLabel;
  /** Show progress indicator on this step */
  showProgress?: boolean;
  /** Role-based visibility (Issue #471) */
  visibleToRoles?: string[];
}

/**
 * Compliance question for intake wizard
 */
export interface IntakeComplianceQuestion {
  /** Unique question identifier */
  id: string;
  /** Question text */
  question: string;
  /** Required answer to proceed (true = must answer yes) */
  requiredAnswer: boolean;
  /** Message shown if answer disqualifies user */
  blockingMessage?: string;
  /** Form field name that, when set to an accepted value, auto-satisfies this question */
  linkedField?: string;
  /** Values of the linked field that satisfy this question (if omitted, any truthy value satisfies) */
  linkedFieldAcceptedValues?: string[];
}

/**
 * Rate calculator configuration for intake wizard
 */
export interface IntakeRateCalculatorConfig {
  /** Maximum LTV percentage */
  maxLTV: number;
  /** Maximum LTC percentage */
  maxLTC: number;
  /** Available term options in months */
  termOptions: number[];
  /** Default interest rate */
  defaultRate: number;
  /** Minimum loan amount */
  minLoanAmount?: number;
  /** Maximum loan amount */
  maxLoanAmount?: number;
  /** Rate adjustments based on conditions */
  rateAdjustments?: Array<{
    condition: string;
    adjustment: number;
    description: string;
  }>;
}

/**
 * Form theming options (Issue #471)
 */
export interface IntakeFormTheme {
  /** Primary color (hex) */
  primaryColor?: string;
  /** Secondary color (hex) */
  secondaryColor?: string;
  /** Background color (hex) */
  backgroundColor?: string;
  /** Text color (hex) */
  textColor?: string;
  /** Font family */
  fontFamily?: string;
  /** Border radius (px) */
  borderRadius?: number;
  /** Custom CSS */
  customCss?: string;
  /** Logo URL */
  logoUrl?: string;
  /** Logo position */
  logoPosition?: 'left' | 'center' | 'right';
  /** Dark mode support */
  supportsDarkMode?: boolean;
  /** Dark mode overrides */
  darkModeOverrides?: Partial<Omit<IntakeFormTheme, 'darkModeOverrides'>>;
}

/**
 * Webhook configuration for form submissions (Issue #471)
 */
export interface IntakeWebhookConfig {
  /** Webhook URL */
  url: string;
  /** Webhook name for display */
  name?: string;
  /** HTTP method */
  method?: 'POST' | 'PUT';
  /** Custom headers */
  headers?: Record<string, string>;
  /** Include form data in payload */
  includeFormData?: boolean;
  /** Include rate calculation in payload */
  includeRateCalc?: boolean;
  /** Trigger conditions */
  triggerOn?: 'submission' | 'qualified' | 'converted';
  /** Secret for HMAC signing */
  secret?: string;
  /** Enable/disable */
  enabled?: boolean;
}

/**
 * Submission routing rule (Issue #471)
 */
export interface IntakeRoutingConfig {
  /** Rule name */
  name: string;
  /** Priority (higher = checked first) */
  priority: number;
  /** Conditions to match */
  conditions: IntakeConditionGroup;
  /** Action to take */
  action: {
    /** Assign to specific user ID */
    assignToUserId?: string;
    /** Assign to team (round-robin) */
    assignToTeamId?: string;
    /** Send notification to emails */
    notifyEmails?: string[];
    /** Override status */
    setStatus?: string;
    /** Apply tags */
    applyTags?: string[];
    /** Trigger webhook */
    webhookUrl?: string;
  };
}

/**
 * Analytics configuration (Issue #471)
 */
export interface IntakeAnalyticsConfig {
  /** Track field-level timing */
  trackFieldTiming?: boolean;
  /** Track abandonment points */
  trackAbandonment?: boolean;
  /** Track error frequency */
  trackErrors?: boolean;
  /** Send events to external analytics */
  externalAnalytics?: {
    /** Google Analytics measurement ID */
    googleAnalyticsId?: string;
    /** Segment write key */
    segmentWriteKey?: string;
    /** Custom analytics endpoint */
    customEndpoint?: string;
  };
}

/**
 * Intake flow template data payload (Issue #471 enhanced)
 */
export interface IntakeFlowTemplateData {
  /** Intake flow type */
  intakeType: IntakeFlowType;
  /** Template version (semver format) */
  version: string;
  /** Wizard steps */
  steps: IntakeStepTemplate[];
  /** Rate calculator configuration */
  rateCalculatorConfig?: IntakeRateCalculatorConfig;
  /** Compliance questions */
  complianceQuestions: IntakeComplianceQuestion[];
  /** What happens after completion */
  exitBehavior: 'case_creation' | 'advisor_redirect' | 'custom' | 'warn';
  /** Redirect URL for advisor flow */
  advisorRedirectUrl?: string;
  /** Custom redirect URL when exitBehavior is 'custom' */
  customExitUrl?: string;

  // Issue #471 - Enhanced features
  /**
   * Display mode for the wizard
   * @default IntakeDisplayMode.WIZARD
   */
  displayMode?: IntakeDisplayMode;
  /** Form theming options */
  theme?: IntakeFormTheme;
  /** Supported languages (ISO codes) */
  supportedLanguages?: string[];
  /** Default language */
  defaultLanguage?: string;
  /** Post-submission webhooks */
  webhooks?: IntakeWebhookConfig[];
  /** Submission routing rules */
  routingRules?: IntakeRoutingConfig[];
  /** Analytics configuration */
  analytics?: IntakeAnalyticsConfig;
  /** Auto-save draft interval (seconds, 0 to disable) */
  autoSaveInterval?: number;
  /** Show estimated completion time */
  showEstimatedTime?: boolean;
  /** Estimated completion time (minutes) */
  estimatedTimeMinutes?: number;
  /** Enable mobile preview in editor */
  mobilePreviewEnabled?: boolean;
  /** Custom success message */
  successMessage?: string | IntakeI18nLabel;
  /** Custom success redirect URL */
  successRedirectUrl?: string;
  /** PDF generation options */
  pdfConfig?: {
    /** Enable PDF generation */
    enabled?: boolean;
    /** Include rate calculation */
    includeRateCalc?: boolean;
    /** Include branding */
    includeBranding?: boolean;
    /** Template name for PDF */
    templateName?: string;
  };
}

export type IntakeFlowTemplate = SystemTemplate<IntakeFlowTemplateData>;

/**
 * Intake submission status (Issue #471)
 */
export type IntakeSubmissionStatus = 'submitted' | 'reviewing' | 'qualified' | 'converted' | 'rejected';

/**
 * Intake draft data (Issue #471)
 */
export interface IntakeDraftData {
  /** Draft ID */
  id: string;
  /** Business ID */
  businessId: string;
  /** Template ID */
  templateId: string;
  /** Template type (system or custom) */
  templateType: 'system' | 'custom';
  /** Template version at draft creation */
  templateVersion: number;
  /** Consumer ID if authenticated */
  consumerId?: string;
  /** Session ID for anonymous users */
  sessionId?: string;
  /** Email if collected */
  email?: string;
  /** Current step index */
  currentStep: number;
  /** Completed step indices */
  completedSteps: number[];
  /** Form data collected so far */
  formData: Record<string, unknown>;
  /** Rate calculation snapshot */
  rateSnapshot?: Record<string, unknown>;
  /** Attribution */
  referralCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  /** Timestamps */
  lastActivityAt: Date;
  expiresAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

/**
 * Intake submission data (Issue #471)
 */
export interface IntakeSubmissionData {
  /** Submission ID */
  id: string;
  /** Human-readable submission number */
  submissionNumber: string;
  /** Business ID */
  businessId: string;
  /** Template reference */
  templateId: string;
  templateType: 'system' | 'custom';
  templateVersion: number;
  templateName: string;
  /** Consumer info */
  consumerId?: string;
  email: string;
  phone?: string;
  name?: string;
  /** Form data */
  formData: Record<string, unknown>;
  intakeType: IntakeFlowType;
  /** Rate calculation */
  calculatedRate?: number;
  loanAmount?: number;
  purchasePrice?: number;
  ltv?: number;
  ltc?: number;
  estimatedMonthly?: number;
  /** Property info */
  propertyAddress?: string;
  propertyCity?: string;
  propertyState?: string;
  propertyZip?: string;
  propertyType?: string;
  /** Processing */
  status: IntakeSubmissionStatus;
  assignedTo?: string;
  assignedAt?: Date;
  /** Conversion */
  convertedToCaseId?: string;
  convertedAt?: Date;
  convertedBy?: string;
  /** Rejection */
  rejectedAt?: Date;
  rejectedBy?: string;
  rejectionReason?: string;
  /** Attribution */
  referralCode?: string;
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  sourceUrl?: string;
  /** Analytics */
  completionTimeSeconds?: number;
  stepsCompleted: number;
  totalSteps: number;
  /** Timestamps */
  createdAt: Date;
  updatedAt: Date;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Union of all template types
 */
export type AnySystemTemplate =
  | SequenceTemplate
  | CampaignTemplate
  | SavedSearchTemplate
  | PipelineTemplate
  | TermsheetTemplate
  | IntakeFlowTemplate;

/**
 * Template type discriminator
 */
export type TemplateType = 'sequence' | 'campaign' | 'saved-search' | 'pipeline' | 'termsheet' | 'intake-flow';

/**
 * Get template by type
 */
export type TemplateByType<T extends TemplateType> = T extends 'sequence'
  ? SequenceTemplate
  : T extends 'campaign'
    ? CampaignTemplate
    : T extends 'saved-search'
      ? SavedSearchTemplate
      : T extends 'pipeline'
        ? PipelineTemplate
        : T extends 'termsheet'
          ? TermsheetTemplate
          : T extends 'intake-flow'
            ? IntakeFlowTemplate
            : never;

// =============================================================================
// USER TEMPLATE TYPES (Issue #400 - Custom Template CRUD)
// =============================================================================

/**
 * User template type enum - matches Prisma UserTemplateType
 */
export type UserTemplateType = 'SEQUENCE' | 'CAMPAIGN' | 'PIPELINE' | 'SAVED_SEARCH' | 'TERMSHEET' | 'INTAKE_FLOW';

/**
 * Map TemplateType string to UserTemplateType enum
 */
export const TEMPLATE_TYPE_TO_USER_TYPE: Record<TemplateType, UserTemplateType> = {
  sequence: 'SEQUENCE',
  campaign: 'CAMPAIGN',
  'saved-search': 'SAVED_SEARCH',
  pipeline: 'PIPELINE',
  termsheet: 'TERMSHEET',
  'intake-flow': 'INTAKE_FLOW',
};

/**
 * Map UserTemplateType enum to TemplateType string
 */
export const USER_TYPE_TO_TEMPLATE_TYPE: Record<UserTemplateType, TemplateType> = {
  SEQUENCE: 'sequence',
  CAMPAIGN: 'campaign',
  SAVED_SEARCH: 'saved-search',
  PIPELINE: 'pipeline',
  TERMSHEET: 'termsheet',
  INTAKE_FLOW: 'intake-flow',
};

/**
 * User-created template stored in the database
 */
export interface UserTemplate {
  /** Unique identifier */
  id: string;
  /** Business ID (multi-tenancy) */
  businessId: string;

  // Template identification
  /** Type of template */
  templateType: UserTemplateType;
  /** Display name */
  name: string;
  /** Description */
  description?: string | null;

  // Display/organization
  /** User-defined category */
  category?: string | null;
  /** Material icon name */
  icon?: string | null;
  /** Accent color (hex) */
  color?: string | null;
  /** Searchable tags */
  tags: string[];

  // Complexity indicator
  /** Complexity level */
  complexity: TemplateComplexity;

  // Template data
  /** Template-specific payload (structure varies by templateType) */
  data: unknown;

  // Source tracking
  /** ID of system template this was copied from */
  sourceTemplateId?: string | null;
  /** Name of system template (denormalized) */
  sourceTemplateName?: string | null;

  // Sharing/visibility
  /** If true, visible to all users in business */
  isPublic: boolean;
  /** If true, shown as default option */
  isDefault: boolean;

  // Admin-seeded templates
  /** True if pushed by admin portal */
  isAdminSeeded: boolean;
  /** Original template ID if admin-seeded */
  seededFromId?: string | null;
  /** When it was seeded */
  seededAt?: Date | null;
  /** Admin user ID who seeded it */
  seededBy?: string | null;

  // Usage tracking
  /** How many times used to create entities */
  usageCount: number;
  /** When last used */
  lastUsedAt?: Date | null;

  // Audit fields
  /** User ID who created */
  createdBy: string;
  /** User name (denormalized) */
  createdByName?: string | null;
  /** Created timestamp */
  createdAt: Date;
  /** Last updated timestamp */
  updatedAt: Date;

  // Soft delete
  /** Deleted timestamp */
  deletedAt?: Date | null;
  /** User ID who deleted */
  deletedBy?: string | null;
}

/**
 * Input for creating a user template
 */
export interface CreateUserTemplateInput {
  templateType: UserTemplateType;
  name: string;
  description?: string;
  category?: string;
  icon?: string;
  color?: string;
  tags?: string[];
  complexity?: TemplateComplexity;
  data: unknown;
  sourceTemplateId?: string;
  sourceTemplateName?: string;
  isPublic?: boolean;
  isDefault?: boolean;
}

/**
 * Input for updating a user template
 */
export interface UpdateUserTemplateInput {
  name?: string;
  description?: string;
  category?: string;
  icon?: string;
  color?: string;
  tags?: string[];
  complexity?: TemplateComplexity;
  data?: unknown;
  isPublic?: boolean;
  isDefault?: boolean;
}

/**
 * Filter options for listing user templates
 */
export interface UserTemplateFilters {
  templateType?: UserTemplateType;
  category?: string;
  search?: string;
  isPublic?: boolean;
  isDefault?: boolean;
  isAdminSeeded?: boolean;
  createdBy?: string;
}

// =============================================================================
// BUSINESS ONBOARDING WIZARD TYPES (Issue #435)
// =============================================================================
// Template-driven business onboarding intake wizard that collects P0 information
// from business admins during their first login experience.
//
// Key Features:
// - Shows on every login until all required questions are answered
// - Business admins can skip the wizard each time (goes to portal)
// - Admin portal users can prefill answers on behalf of businesses
// - Uses the IntakeWizard component from design-system-ui

/**
 * Business onboarding question categories
 */
export type OnboardingQuestionCategory =
  | 'company_info'
  | 'lending_capabilities'
  | 'licensing'
  | 'team_structure'
  | 'integrations'
  | 'compliance'
  | 'preferences';

/**
 * Onboarding field template - reuses IntakeFieldTemplate with additional properties
 */
export interface OnboardingFieldTemplate extends IntakeFieldTemplate {
  /** Priority level (P0 = must have, P1 = important, P2 = nice to have) */
  priority: 'P0' | 'P1' | 'P2';
  /** Category for grouping */
  category: OnboardingQuestionCategory;
  /** Maps to Business schema field (if directly storable) */
  businessFieldMapping?: string;
  /** Admin can prefill this field */
  adminPrefilable?: boolean;
}

/**
 * Onboarding step template - reuses IntakeStepTemplate pattern
 */
export interface OnboardingStepTemplate {
  /** Unique step identifier */
  id: string;
  /** Step order (0-based) */
  order: number;
  /** Step display name */
  name: string;
  /** Step description */
  description: string;
  /** Material icon name */
  icon: string;
  /** Category for this step */
  category: OnboardingQuestionCategory;
  /** Fields in this step */
  fields: OnboardingFieldTemplate[];
  /** Whether this step is skippable */
  skippable: boolean;
}

/**
 * Business onboarding wizard template data
 */
export interface OnboardingWizardTemplateData {
  /** Template version */
  version: string;
  /** Wizard steps */
  steps: OnboardingStepTemplate[];
  /** Exit behavior after completion */
  exitBehavior: 'dashboard' | 'settings' | 'custom';
  /** Custom redirect URL */
  redirectUrl?: string;
  /** Success message shown after completion */
  completionMessage: string;
  /** Whether admins can prefill for businesses */
  adminPrefilEnabled: boolean;
}

export type OnboardingWizardTemplate = SystemTemplate<OnboardingWizardTemplateData>;

/**
 * Business onboarding response status
 */
export type OnboardingStatus = 'not_started' | 'in_progress' | 'completed' | 'skipped_indefinitely';

/**
 * Business onboarding response (stored per business)
 */
export interface BusinessOnboardingResponse {
  /** Unique identifier */
  id: string;
  /** Business ID */
  businessId: string;
  /** Template ID used */
  templateId: string;
  /** Template version (for migration tracking) */
  templateVersion: string;
  /** Current status */
  status: OnboardingStatus;
  /** Current step (if in_progress) */
  currentStep?: number;
  /** Collected responses (JSON) */
  responses: Record<string, unknown>;
  /** Fields that were prefilled by admin */
  adminPrefilledFields: string[];
  /** User ID who prefilled (if any) */
  prefilledBy?: string;
  /** When prefilled */
  prefilledAt?: Date;
  /** Number of times skipped */
  skipCount: number;
  /** Last skipped timestamp */
  lastSkippedAt?: Date;
  /** Completed timestamp */
  completedAt?: Date;
  /** User ID who completed */
  completedBy?: string;
  /** Created timestamp */
  createdAt: Date;
  /** Updated timestamp */
  updatedAt: Date;
}

/**
 * Input for saving onboarding progress
 */
export interface SaveOnboardingProgressInput {
  templateId: string;
  currentStep: number;
  responses: Record<string, unknown>;
}

/**
 * Input for admin prefilling onboarding
 */
export interface AdminPrefillOnboardingInput {
  businessId: string;
  templateId: string;
  responses: Record<string, unknown>;
}
